/**
 * Frontend-specific error handling utilities
 *
 * This module provides utilities for parsing and handling standardized errors
 * from the backend in frontend applications (web and desktop).
 */

import type { ErrorCategory, ErrorContext, ErrorResponse } from "../types/index";
import { BaseError } from "../classes/BaseError";
import {
  ValidationError,
  AuthenticationError,
  AuthorizationError,
  NotFoundError,
  ConflictError,
  InternalError,
  RateLimitError,
  NetworkError,
  DatabaseError,
  ExternalServiceError,
} from "../classes/index";
import {
  VALIDATION_ERROR_CODES,
  AUTHENTICATION_ERROR_CODES,
  AUTHORIZATION_ERROR_CODES,
  NOT_FOUND_ERROR_CODES,
  CONFLICT_ERROR_CODES,
  INTERNAL_ERROR_CODES,
  RATE_LIMIT_ERROR_CODES,
  NETWORK_ERROR_CODES,
  DATABASE_ERROR_CODES,
  EXTERNAL_SERVICE_ERROR_CODES,
  FRONTEND_ERROR_CODES,
} from "../constants/errorCodes";
import { FrontEndError } from "./FrontEndError";

const ERROR_CATEGORIES: ErrorCategory[] = [
  "validation",
  "authentication",
  "authorization",
  "not_found",
  "conflict",
  "rate_limit",
  "network",
  "database",
  "external_service",
  "internal",
  "frontend",
  "configuration",
  "business_logic",
];

function isErrorCategory(value: unknown): value is ErrorCategory {
  return typeof value === "string" && ERROR_CATEGORIES.includes(value as ErrorCategory);
}

/**
 * Parse a standardized error response from the backend
 */
export function parseErrorResponse(response: unknown): ErrorResponse | null {
  if (!response || typeof response !== "object") {
    return null;
  }
  const responseObj = response as Record<string, unknown>;

  // Check if it's already a properly formatted ErrorResponse
  if (
    responseObj.message &&
    responseObj.code &&
    responseObj.statusCode &&
    responseObj.timestamp
  ) {
    return response as ErrorResponse;
  }

  // Try to extract error information from common response formats
  const errorData = responseObj.error || responseObj.data || responseObj;

  if (errorData && typeof errorData === "object") {
    const errorObj = errorData as Record<string, unknown>;
    const parsedContext =
      errorObj.context && typeof errorObj.context === "object"
        ? (errorObj.context as ErrorContext)
        : undefined;
    return {
      message: typeof errorObj.message === "string" ? errorObj.message : "An error occurred",
      code: typeof errorObj.code === "string" ? errorObj.code : "UNKNOWN_ERROR",
      category: isErrorCategory(errorObj.category) ? errorObj.category : "internal",
      statusCode: typeof errorObj.statusCode === "number" ? errorObj.statusCode : 500,
      requestId: typeof errorObj.requestId === "string" ? errorObj.requestId : undefined,
      timestamp:
        typeof errorObj.timestamp === "string" ? errorObj.timestamp : new Date().toISOString(),
      field: typeof errorObj.field === "string" ? errorObj.field : undefined,
      value: errorObj.value,
      resource: typeof errorObj.resource === "string" ? errorObj.resource : undefined,
      id: typeof errorObj.id === "string" ? errorObj.id : undefined,
      context: parsedContext,
      userVisible: typeof errorObj.userVisible === "boolean" ? errorObj.userVisible : undefined,
    };
  }

  return null;
}

/**
 * Convert an ErrorResponse to the appropriate BaseError instance
 */
export function createErrorFromResponse(errorResponse: ErrorResponse): BaseError {
  const { code, message, statusCode, field, value, resource, id, context, userVisible } =
    errorResponse;

  // Map error codes to appropriate error classes
  switch (code) {
    // Validation errors
    case VALIDATION_ERROR_CODES.VALIDATION_ERROR:
    case VALIDATION_ERROR_CODES.INVALID_INPUT:
    case VALIDATION_ERROR_CODES.INVALID_FORMAT:
    case VALIDATION_ERROR_CODES.MISSING_REQUIRED_FIELD:
    case VALIDATION_ERROR_CODES.VALUE_OUT_OF_RANGE:
    case VALIDATION_ERROR_CODES.VALUE_TOO_LONG:
    case VALIDATION_ERROR_CODES.VALUE_TOO_SHORT:
    case VALIDATION_ERROR_CODES.INVALID_CHARACTERS:
      return new ValidationError({
        message,
        code,
        field,
        value,
        context,
      });

    // Authentication errors
    case AUTHENTICATION_ERROR_CODES.AUTHENTICATION_ERROR:
    case AUTHENTICATION_ERROR_CODES.AUTHENTICATION_FAILED:
    case AUTHENTICATION_ERROR_CODES.INVALID_TOKEN:
    case AUTHENTICATION_ERROR_CODES.TOKEN_EXPIRED:
    case AUTHENTICATION_ERROR_CODES.TOKEN_MISSING:
    case AUTHENTICATION_ERROR_CODES.ACCOUNT_LOCKED:
    case AUTHENTICATION_ERROR_CODES.ACCOUNT_UNVERIFIED:
    case AUTHENTICATION_ERROR_CODES.TOO_MANY_ATTEMPTS:
    case AUTHENTICATION_ERROR_CODES.TWO_FACTOR_REQUIRED:
    case AUTHENTICATION_ERROR_CODES.TWO_FACTOR_FAILED:
      return new AuthenticationError({
        message,
        code,
        context,
      });

    // Authorization errors
    case AUTHORIZATION_ERROR_CODES.AUTHORIZATION_ERROR:
    case AUTHORIZATION_ERROR_CODES.AUTHORIZATION_DENIED:
    case AUTHORIZATION_ERROR_CODES.ACCESS_FORBIDDEN:
    case AUTHORIZATION_ERROR_CODES.INSUFFICIENT_PERMISSIONS:
    case AUTHORIZATION_ERROR_CODES.ORGANIZATION_ACCESS_DENIED:
    case AUTHORIZATION_ERROR_CODES.TEAM_ACCESS_DENIED:
    case AUTHORIZATION_ERROR_CODES.ROLE_ACCESS_DENIED:
      return new AuthorizationError({
        message,
        code,
        context,
      });

    // Not found errors
    case NOT_FOUND_ERROR_CODES.NOT_FOUND_ERROR:
    case NOT_FOUND_ERROR_CODES.ENTITY_NOT_FOUND:
    case NOT_FOUND_ERROR_CODES.USER_NOT_FOUND:
    case NOT_FOUND_ERROR_CODES.ORGANIZATION_NOT_FOUND:
    case NOT_FOUND_ERROR_CODES.TEAM_NOT_FOUND:
    case NOT_FOUND_ERROR_CODES.RESOURCE_NOT_FOUND:
    case NOT_FOUND_ERROR_CODES.FILE_NOT_FOUND:
    case NOT_FOUND_ERROR_CODES.ROUTE_NOT_FOUND:
    case NOT_FOUND_ERROR_CODES.ENDPOINT_NOT_FOUND:
      return new NotFoundError({
        message,
        code,
        resource,
        id,
        context,
      });

    // Conflict errors
    case CONFLICT_ERROR_CODES.CONFLICT_ERROR:
    case CONFLICT_ERROR_CODES.DUPLICATE_ENTITY:
    case CONFLICT_ERROR_CODES.EMAIL_ALREADY_EXISTS:
    case CONFLICT_ERROR_CODES.USERNAME_ALREADY_EXISTS:
    case CONFLICT_ERROR_CODES.RESOURCE_ALREADY_EXISTS:
    case CONFLICT_ERROR_CODES.CONCURRENT_MODIFICATION:
    case CONFLICT_ERROR_CODES.VERSION_CONFLICT:
    case CONFLICT_ERROR_CODES.STATE_CONFLICT:
      return new ConflictError({
        message,
        code,
        context,
      });

    // Rate limit errors
    case RATE_LIMIT_ERROR_CODES.RATE_LIMIT_EXCEEDED:
    case RATE_LIMIT_ERROR_CODES.TOO_MANY_REQUESTS:
    case RATE_LIMIT_ERROR_CODES.QUOTA_EXCEEDED:
    case RATE_LIMIT_ERROR_CODES.RESOURCE_EXHAUSTED:
    case RATE_LIMIT_ERROR_CODES.BANDWIDTH_LIMIT_EXCEEDED:
      return new RateLimitError({
        message,
        code,
        context,
      });

    // Network errors
    case NETWORK_ERROR_CODES.NETWORK_ERROR:
    case NETWORK_ERROR_CODES.CONNECTION_TIMEOUT:
    case NETWORK_ERROR_CODES.REQUEST_TIMEOUT:
    case NETWORK_ERROR_CODES.CONNECTION_REFUSED:
    case NETWORK_ERROR_CODES.NETWORK_UNREACHABLE:
      return new NetworkError({
        message,
        code,
        context,
      });

    // Database errors
    case DATABASE_ERROR_CODES.DATABASE_ERROR:
    case DATABASE_ERROR_CODES.DATABASE_CONNECTION_FAILED:
    case DATABASE_ERROR_CODES.DATABASE_QUERY_FAILED:
    case DATABASE_ERROR_CODES.DATABASE_TRANSACTION_FAILED:
    case DATABASE_ERROR_CODES.DATABASE_CONSTRAINT_VIOLATION:
    case DATABASE_ERROR_CODES.DATABASE_DEADLOCK:
      return new DatabaseError({
        message,
        code,
        context,
      });

    // External service errors
    case EXTERNAL_SERVICE_ERROR_CODES.EXTERNAL_SERVICE_ERROR:
    case EXTERNAL_SERVICE_ERROR_CODES.SERVICE_UNAVAILABLE:
    case EXTERNAL_SERVICE_ERROR_CODES.SERVICE_TIMEOUT:
    case EXTERNAL_SERVICE_ERROR_CODES.SERVICE_RATE_LIMIT_EXCEEDED:
    case EXTERNAL_SERVICE_ERROR_CODES.SERVICE_AUTHENTICATION_FAILED:
    case EXTERNAL_SERVICE_ERROR_CODES.SERVICE_AUTHORIZATION_FAILED:
    case EXTERNAL_SERVICE_ERROR_CODES.SERVICE_QUOTA_EXCEEDED:
      return new ExternalServiceError({
        message,
        code,
        context,
      });

    // Internal errors
    case INTERNAL_ERROR_CODES.INTERNAL_ERROR:
    case INTERNAL_ERROR_CODES.UNEXPECTED_ERROR:
    case INTERNAL_ERROR_CODES.SYSTEM_ERROR:
    case INTERNAL_ERROR_CODES.CONFIGURATION_ERROR:
    case INTERNAL_ERROR_CODES.NOT_IMPLEMENTED:
    case INTERNAL_ERROR_CODES.TEMPORARILY_UNAVAILABLE:
      return new InternalError({
        message,
        code,
        context,
      });

    // Frontend errors
    case FRONTEND_ERROR_CODES.FRONTEND_ERROR:
      return new FrontEndError({
        message,
        code,
        context,
      });

    // Default to BaseError for unknown codes
    default:
      return new BaseError({
        message,
        code,
        category: errorResponse.category || "internal",
        statusCode,
        context,
        userVisible,
      });
  }
}

/**
 * Handle an error from an API response, converting it to the appropriate error type
 */
export function handleApiError(error: unknown): BaseError {
  // If it's already a BaseError, return it
  if (error instanceof BaseError) {
    return error;
  }

  const errorResponse = parseErrorResponse(error);
  if (errorResponse) {
    return createErrorFromResponse(errorResponse);
  }

  // Handle Axios errors
  if (typeof error === "object" && error !== null && "response" in error) {
    const responseContainer = (error as { response?: { data?: unknown; status?: number } }).response;
    const responseData = responseContainer?.data;
    const errorResponse = parseErrorResponse(responseData);
    if (errorResponse) {
      return createErrorFromResponse(errorResponse);
    }

    // Fallback to status code-based error creation
    return createErrorFromStatus(
      responseContainer?.status ?? 500,
      error instanceof Error ? error.message : undefined,
    );
  }

  // Handle network errors (no response)
  if (typeof error === "object" && error !== null && "request" in error) {
    return new NetworkError({
      message: "Network error - please check your connection",
      code: NETWORK_ERROR_CODES.NETWORK_ERROR,
      context: {
        metadata: {
          originalError: error instanceof Error ? error.message : String(error),
        },
      },
    });
  }

  // Handle other errors
  return new InternalError({
    message: error instanceof Error ? error.message : "An unexpected error occurred",
    code: INTERNAL_ERROR_CODES.UNEXPECTED_ERROR,
    context: {
      metadata: {
        originalError: error,
      },
    },
  });
}

/**
 * Handle a frontend error
 */
export function handleFrontendError(error: unknown): FrontEndError {
  if (error instanceof BaseError) {
    return error;
  }

  if (error instanceof Error) {
    return new FrontEndError({
      message: error.message,
      code: FRONTEND_ERROR_CODES.FRONTEND_ERROR,
    });
  }

  return new FrontEndError({
    message: "An unknown error occurred",
    code: FRONTEND_ERROR_CODES.FRONTEND_ERROR,
  });
}

/**
 * Create an error from HTTP status code
 */
function createErrorFromStatus(statusCode: number, message?: string): BaseError {
  const defaultMessage = message || `HTTP ${statusCode} error`;

  switch (statusCode) {
    case 400:
      return new ValidationError({
        message: defaultMessage,
        code: VALIDATION_ERROR_CODES.VALIDATION_ERROR,
      });
    case 401:
      return new AuthenticationError({
        message: defaultMessage,
        code: AUTHENTICATION_ERROR_CODES.AUTHENTICATION_FAILED,
      });
    case 403:
      return new AuthorizationError({
        message: defaultMessage,
        code: AUTHORIZATION_ERROR_CODES.AUTHORIZATION_DENIED,
      });
    case 404:
      return new NotFoundError({
        message: defaultMessage,
        code: NOT_FOUND_ERROR_CODES.ENTITY_NOT_FOUND,
      });
    case 409:
      return new ConflictError({
        message: defaultMessage,
        code: CONFLICT_ERROR_CODES.CONFLICT_ERROR,
      });
    case 429:
      return new RateLimitError({
        message: defaultMessage,
        code: RATE_LIMIT_ERROR_CODES.RATE_LIMIT_EXCEEDED,
      });
    case 500:
    case 502:
    case 503:
    case 504:
      return new InternalError({
        message: defaultMessage,
        code: INTERNAL_ERROR_CODES.INTERNAL_ERROR,
      });
    default:
      return new BaseError({
        message: defaultMessage,
        code: "HTTP_ERROR",
        category: "internal",
        statusCode,
      });
  }
}

// Note: isUserVisibleError, getUserFriendlyMessage, and getErrorSeverity are exported from utils and mappers modules

/**
 * Check if an error should trigger a retry
 */
export function shouldRetryError(error: BaseError): boolean {
  // Retry on network errors, rate limits, and some server errors
  return (
    error instanceof NetworkError ||
    error instanceof RateLimitError ||
    (error instanceof InternalError && (error.statusCode ?? 0) >= 500)
  );
}

/**
 * Get retry delay in milliseconds
 */
export function getRetryDelay(error: BaseError, attempt: number): number {
  if (error instanceof RateLimitError) {
    // Longer delay for rate limits
    return Math.min(1000 * Math.pow(2, attempt), 30000); // Max 30 seconds
  }

  if (error instanceof NetworkError) {
    // Exponential backoff for network errors
    return Math.min(1000 * Math.pow(2, attempt), 10000); // Max 10 seconds
  }

  // Default retry delay
  return Math.min(1000 * attempt, 5000); // Max 5 seconds
}
